import type { ApiError as ApiErrorBody } from '@replay/shared';

export type ErrorKind = ApiErrorBody['error']['kind'] | 'network';

/** Client-side error with a kind, so the UI can tell "try again" from "this won't work". */
export class ApiError extends Error {
  constructor(
    public kind: ErrorKind,
    public code: string,
    message: string,
    public status: number,
    public details?: unknown,
  ) {
    super(message);
  }
  get retryable() {
    return this.kind === 'network' || this.kind === 'transient';
  }
}

type Json = Record<string, unknown> | unknown[];

export async function api<T = unknown>(
  path: string,
  opts: { method?: string; body?: Json | Blob | ArrayBuffer | Uint8Array; query?: Record<string, string | number | boolean | undefined | null>; signal?: AbortSignal; raw?: boolean } = {},
): Promise<T> {
  let url = path;
  if (opts.query) {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(opts.query)) if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    const s = q.toString();
    if (s) url += (url.includes('?') ? '&' : '?') + s;
  }
  const headers: Record<string, string> = {};
  let body: BodyInit | undefined;
  if (opts.body instanceof Blob || opts.body instanceof ArrayBuffer || opts.body instanceof Uint8Array) {
    headers['content-type'] = opts.body instanceof Blob && opts.body.type ? opts.body.type : 'application/octet-stream';
    body = opts.body as BodyInit;
  } else if (opts.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  let res: Response;
  try {
    res = await fetch(url, { method: opts.method ?? (body ? 'POST' : 'GET'), headers, body, credentials: 'same-origin', signal: opts.signal });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    window.dispatchEvent(new CustomEvent('replay:network', { detail: false }));
    throw new ApiError('network', 'network', 'Sin conexión con REPLAY. Comprueba tu red.', 0);
  }
  if (res.status < 500) window.dispatchEvent(new CustomEvent('replay:network', { detail: true }));
  if (!res.ok) {
    let parsed: ApiErrorBody | null = null;
    try {
      parsed = (await res.json()) as ApiErrorBody;
    } catch {
      /* not json */
    }
    const e = parsed?.error;
    const kind: ErrorKind = e?.kind ?? (res.status >= 500 ? 'transient' : 'permanent');
    const err = new ApiError(kind, e?.code ?? `http_${res.status}`, e?.message ?? `Error ${res.status}`, res.status, e?.details);
    if (res.status === 401 && kind === 'auth') window.dispatchEvent(new CustomEvent('replay:unauthenticated'));
    throw err;
  }
  if (opts.raw) return res as unknown as T;
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  return (ct.includes('application/json') ? res.json() : res.arrayBuffer()) as Promise<T>;
}

export const errorMessage = (e: unknown) => (e instanceof Error ? e.message : 'Algo ha fallado');
